import React, { memo, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { Divider, useMediaQuery } from '@mui/material';
import { UiIcon, UiLoading, UiSelect, UiTooltip } from 'ui-components';
import { usePermission, usePlatform } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  setCurrentQuery,
  setQueries,
  applyFiltersToApiRequest,
  applyFiltersToAllPlatforms,
  setPlatformCurrentPageNumber,
} from 'store';
import { isEmpty } from 'lib';
import { magicNumbers } from 'utils';
import { StyledQueryFilter, StyledQueryMenuItem } from './QueryFilter.styles';
import LockedFreeSearchChip from './locked-free-search-chip/LockedFreeSearchChip';

const LOCKED_FREE_SEARCH_VALUE = 'locked-free-search';

const QueryFilter = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { userPermission } = usePermission();
  const currentQuery = useSelector((state) => state.pageReducer.currentQuery);
  const queries = useSelector((state) => state.pageReducer.queries);
  const { getSettingsDefaultPlatform } = usePlatform();
  const { platform: activePlatform = getSettingsDefaultPlatform() } =
    useParams();
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('sm'));

  const { permission: permi_frontEnd_searchByQuery } = useMemo(
    () => userPermission('FrontEnd', 'searchByQuery'),
    [userPermission]
  );

  const { permission: permi_queryRules_index } = useMemo(
    () => userPermission('QueryRules', 'index'),
    [userPermission]
  );

  const { permission: hasFreeSearchAccess } = useMemo(
    () => userPermission('FrontEnd', 'freeSearch'),
    [userPermission]
  );

  const { permission: hasMegaSubjectPermission } = useMemo(
    () => userPermission('FrontEnd', 'megaSubject'),
    [userPermission]
  );

  const hasMegaSubjectAccess = hasFreeSearchAccess && hasMegaSubjectPermission;
  const showFreeSearchOption = !hasMegaSubjectAccess && hasFreeSearchAccess;
  const showLockedFreeSearchChip =
    isDesktop && !hasMegaSubjectAccess && !hasFreeSearchAccess;
  const showLockedFreeSearchMenuItem =
    !hasMegaSubjectAccess && !hasFreeSearchAccess;
  const hasSpecialOption =
    hasMegaSubjectAccess ||
    showFreeSearchOption ||
    showLockedFreeSearchMenuItem;

  const subjectQueries = useMemo(
    () =>
      (queries.data || []).filter(
        (item) =>
          item.id !== magicNumbers.YOUR_SEARCH_QUERY_ID &&
          item.id !== magicNumbers.MEGA_SUBJECT_QUERY_ID
      ),
    [queries.data]
  );

  const isMegaSubjectSelected =
    currentQuery.queryId === magicNumbers.MEGA_SUBJECT_QUERY_ID;
  const isFreeSearchSelected =
    currentQuery.queryId === magicNumbers.YOUR_SEARCH_QUERY_ID;

  const selectedLabel = useMemo(() => {
    if (isFreeSearchSelected) {
      return t('layout:freeSearch');
    }
    if (isMegaSubjectSelected) {
      return t('common:metaSubject');
    }
    return (
      queries?.data?.find((item) => item.id === currentQuery.queryId)?.title ||
      ''
    );
  }, [
    currentQuery.queryId,
    isFreeSearchSelected,
    isMegaSubjectSelected,
    queries?.data,
    t,
  ]);

  const selectedIcon = useMemo(() => {
    if (isMegaSubjectSelected) return 'ri-infinity-line';
    if (isFreeSearchSelected) return 'ri-search-line';
    return 'ri-focus-2-line';
  }, [isFreeSearchSelected, isMegaSubjectSelected]);

  const handleQueryChange = (event) => {
    const nextValue = event.target.value;
    if (nextValue === LOCKED_FREE_SEARCH_VALUE) {
      return;
    }

    if (
      nextValue === magicNumbers.YOUR_SEARCH_QUERY_ID &&
      showFreeSearchOption
    ) {
      /**
       * Remember the subject in use so an empty free search keeps requesting it
       * instead of falling through to an unrestricted, mega-subject-like query.
       * Re-selecting free search must not overwrite it with free search itself.
       */
      const isSubjectSelected =
        Boolean(currentQuery.queryId) &&
        currentQuery.queryId !== magicNumbers.YOUR_SEARCH_QUERY_ID &&
        currentQuery.queryId !== magicNumbers.MEGA_SUBJECT_QUERY_ID;

      dispatch(
        setQueries(
          (queries.data || []).filter(
            (item) => item.id !== magicNumbers.YOUR_SEARCH_QUERY_ID
          )
        )
      );
      dispatch(
        setCurrentQuery({
          queryId: magicNumbers.YOUR_SEARCH_QUERY_ID,
          q: '',
          resourceQuery: '',
          lastSearchQuery: '',
          freeSearchFallbackQueryId: isSubjectSelected
            ? currentQuery.queryId
            : currentQuery.freeSearchFallbackQueryId,
        })
      );
      return;
    }

    dispatch(
      setPlatformCurrentPageNumber({ platform: activePlatform, pageNumber: 1 })
    );

    dispatch(
      setCurrentQuery({
        queryId: nextValue,
        freeSearchFallbackQueryId: 0,
      })
    );
    if (!window.location.pathname.includes('contents')) {
      dispatch(applyFiltersToApiRequest());
    }
    dispatch(applyFiltersToAllPlatforms());
  };

  if (!permi_frontEnd_searchByQuery || !permi_queryRules_index) return null;

  const hasMenuItems = subjectQueries.length > 0 || hasSpecialOption;

  return (
    <StyledQueryFilter className='query-filter'>
      {showLockedFreeSearchChip && <LockedFreeSearchChip />}

      {!queries.error && hasMenuItems ? (
        <div className='query-filter__select'>
          <UiLoading spinning={queries.loading}>
            <UiSelect
              value={currentQuery.queryId}
              defaultValue={currentQuery.queryId}
              onChange={handleQueryChange}
              onOpen={() => setIsOpen(true)}
              onClose={() => setIsOpen(false)}
              open={isOpen}
              data-testid='header-query-filter-select'
              inputProps={{ 'data-testid': 'header-query-filter-input' }}
              IconComponent={null}
              displayEmpty={true}
              renderValue={() => (
                <div className='flex query-filter-selectbox'>
                  <div className='query-filter-selectbox__value'>
                    {/**
                     * آیکون موضوع ساده فقط در حالت جمع‌شده‌ی موبایل دیده می‌شود
                     * (آنجا متن پنهان است و تنها آیکون نوع انتخاب را مشخص می‌کند)
                     */}
                    <UiIcon
                      className={classNames(
                        'query-filter-selectbox__mode-icon',
                        {
                          'is-subject-mode':
                            !isMegaSubjectSelected && !isFreeSearchSelected,
                        }
                      )}
                      size={16}
                      icon={selectedIcon}
                    />
                    <span>{selectedLabel}</span>
                  </div>
                  <UiIcon
                    className={`arrow-icon${isOpen ? ' is-open' : ''}`}
                    size={20}
                    icon='ri-arrow-down-s-line'
                  />
                </div>
              )}
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: '191px',
                    minWidth: '191px !important',
                    maxWidth: '191px',
                    padding: '8px',
                    borderRadius: '8px',
                    boxShadow: '0px 3px 4px rgba(0,0,0,0.05)',
                    border: '1px solid #eee',
                    '& .MuiList-root': {
                      padding: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    },
                  },
                },
              }}
            >
              {hasMegaSubjectAccess && (
                <StyledQueryMenuItem
                  value={magicNumbers.MEGA_SUBJECT_QUERY_ID}
                  data-testid='header-query-filter-mega'
                >
                  <div className='query-menu-item'>
                    <div className='query-menu-item__start'>
                      <UiIcon
                        className='query-menu-item__icon'
                        icon='ri-infinity-line'
                        size={16}
                      />
                      <span>{t('common:metaSubject')}</span>
                    </div>
                  </div>
                </StyledQueryMenuItem>
              )}

              {showFreeSearchOption && (
                <StyledQueryMenuItem
                  value={magicNumbers.YOUR_SEARCH_QUERY_ID}
                  data-testid='header-query-filter-free-search'
                >
                  <div className='query-menu-item'>
                    <div className='query-menu-item__start'>
                      <UiIcon
                        className='query-menu-item__icon'
                        icon='ri-search-line'
                        size={16}
                      />
                      <span>{t('layout:freeSearch')}</span>
                    </div>
                  </div>
                </StyledQueryMenuItem>
              )}

              {showLockedFreeSearchMenuItem && (
                <StyledQueryMenuItem
                  value={LOCKED_FREE_SEARCH_VALUE}
                  disabled
                  className='is-locked-item'
                  data-testid='header-query-filter-locked-free-search'
                >
                  <UiTooltip
                    title={t('common:freeSearchNoAccess')}
                    placement='top'
                    enterTouchDelay={0}
                  >
                    <div
                      className='query-menu-item query-menu-item--locked'
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className='query-menu-item__start'>
                        <UiIcon
                          className='query-menu-item__icon'
                          icon='ri-search-line'
                          size={16}
                        />
                        <span>{t('layout:freeSearch')}</span>
                      </div>
                      <UiIcon
                        className='query-menu-item__lock-icon'
                        icon='ri-lock-line'
                        size={16}
                      />
                    </div>
                  </UiTooltip>
                </StyledQueryMenuItem>
              )}

              {hasSpecialOption && subjectQueries.length > 0 && (
                <Divider component='li' sx={{ margin: '0 !important' }} />
              )}

              {subjectQueries.map((item, index) => (
                <StyledQueryMenuItem
                  key={`query${item.id}`}
                  value={item.id}
                  data-testid={`header-query-filter-select-${index}`}
                >
                  <div className='query-menu-item'>
                    <span>{item.title}</span>
                    {!isEmpty(item.shared_source) && (
                      <UiIcon icon='ri-team-line' color='#ffb30f' />
                    )}
                  </div>
                </StyledQueryMenuItem>
              ))}
            </UiSelect>
          </UiLoading>
        </div>
      ) : (
        queries.error && (
          <span className='error'>{t('subject:errorOccuredInGetQuery')}</span>
        )
      )}
    </StyledQueryFilter>
  );
};

export default memo(QueryFilter);
